@if (loading()) {
  <cue-loading />
} @else {
  <div class="inner">
    <div class="header">
      <h2 class="title">{{ title() }}</h2>

      @if (dialogData.mode === 'anonymous') {
        @if (localeService.languageItems.length > 1) {
          <div class="language-wrapper">
            <assist-dropdownlist
              [popupSettings]="{ appendTo: 'component', popupClass: 'language-popup', width: 'auto' }"
              [data]="localeService.languageItems"
              [formControl]="currentLanguageControl"
              textField="text"
              valueField="text"
              class="language-dropdownlist"
            >
              <ng-template let-dataItem valueTemplate>
                <div class="flag-wrapper">
                  <img
                    [src]="'assets/images/flags/' + dataItem?.flag"
                    alt="language-flag"
                    class="flag"
                    height="30"
                    width="30"
                  />
                  <cue-icon [icon]="ICONS.cueArrowDown" />
                </div>
              </ng-template>
              <ng-template itemTemplate let-dataItem>
                <img
                  [src]="'assets/images/flags/' + dataItem.flag"
                  alt="language-flag"
                  class="flag"
                  height="30"
                  width="30"
                />
                <span class="lang-shortcut">{{ dataItem.text }}</span>
              </ng-template>
            </assist-dropdownlist>
          </div>
        }
      }

      <button tabindex="-1" type="button" aria-label="Close" class="close-btn" (click)="dialogRef.close()">
        <cue-icon [icon]="ICONS.cueClose" />
      </button>
    </div>
    <div class="delimiter"></div>
    <div class="content px-[5px]">
      @switch (activeBarItem()) {
        @case ('detail') {
          @if (dialogData.mode === 'anonymous') {
            @if (anonymousResource.hasValue()) {
              <assist-multiview-anonymous-detail
                [loading]="serverDetail.isLoading() || anonymousResource.isLoading()"
                [item]="anonymousResource.value()"
                [qrCodeId]="qrCodeId()"
              />
            }
          } @else {
            @if (serverDetail.hasValue() || anonymousResource.hasValue()) {
              <assist-multiview-detail
                [loading]="serverDetail.isLoading() || anonymousResource.isLoading()"
                [serverDetail]="serverDetail.hasValue() ? serverDetail.value() : anonymousResource.value()"
                [qrCodeId]="qrCodeId()"
                [resource]="resource()"
              />
            }
          }
        }
        @case ('navigation') {
          <assist-multiview-navigation
            [dialogRef]="dialogRef"
            [isAnonymous]="isAnonymous()"
            [areaId]="areaId()"
            [resourceId]="
              resource()?.idForFiltering ??
              (anonymousResource.hasValue() ? anonymousResource.value().resourceId : undefined)
            "
            [qrCodeId]="qrCodeId()"
          />
        }
        @case ('events') {
          <assist-multiview-events [resource]="resource()" [isAnonymous]="isAnonymous()" />
        }
        @case ('requests') {
          <assist-multiview-requests [resource]="resource()" />
        }
        @case ('news') {
          @if (news.hasValue()) {
            <assist-multiview-news [news]="news.value()" [loading]="news.isLoading()" />
          }
        }
        @case ('control') {
          <assist-multiview-control [controls]="controls()" />
        }
        @case ('device') {
          <assist-multiview-device [unitInfos]="unitInfos()" [loading]="false" />
        }
        @case ('more') {
          <assist-multiview-more [qrCodeId]="qrCodeId()" />
        }
        @default {
          <div class="h-full w-full flex items-center justify-center">
            <span class="text-neutral-500">
              @if (this.isResourceQR() || isAnonymousResourceQR()) {
                {{ translation.multiview.resourceNotFound | transloco }}
              } @else {
                {{ translation.multiview.poiNotFound | transloco }}
              }
            </span>
          </div>
        }
      }
    </div>
  </div>
  <div class="bottom-bar">
    @if (isAnonymousResourceQR()) {
      <button
        (click)="setActiveBarItem('detail')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'detail',
        }"
      >
        <cue-icon [icon]="ICONS.cueInfo" [theme]="activeBarItem() === 'detail' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.detail | transloco }}</span>
      </button>
    }

    @if (isResourceQR() && resource()) {
      <button
        (click)="setActiveBarItem('detail')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'detail',
        }"
      >
        <cue-icon [icon]="ICONS.cueInfo" [theme]="activeBarItem() === 'detail' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.detail | transloco }}</span>
      </button>
    }
    @if (hasLocation()) {
      <button
        (click)="setActiveBarItem('navigation')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'navigation',
        }"
      >
        <cue-icon
          [icon]="ICONS.cueNavigationCircle"
          [theme]="activeBarItem() === 'navigation' ? 'primary-500' : 'neutral'"
        />
        <span>{{ translation.multiview.tabs.navigation | transloco }}</span>
      </button>
    }

    @if (isResourceQR() && resource()) {
      <button
        (click)="setActiveBarItem('events')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'events',
        }"
      >
        <cue-icon [icon]="ICONS.cueCalendarCheck" [theme]="activeBarItem() === 'events' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.events | transloco }}</span>
      </button>
    }

    @if (isResourceQR() && hasServicesAvailable.value()) {
      <button
        (click)="setActiveBarItem('requests')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'requests',
        }"
      >
        <cue-icon [icon]="ICONS.cueOrders" [theme]="activeBarItem() === 'requests' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.requests | transloco }}</span>
      </button>
    }
    @if (isNewsQR()) {
      <button
        (click)="setActiveBarItem('news')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'news',
        }"
      >
        <cue-icon [icon]="ICONS.cueNews" [theme]="activeBarItem() === 'news' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.news | transloco }}</span>
      </button>
    }

    @if (controls()?.length > 0) {
      <button
        (click)="setActiveBarItem('control')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'control',
        }"
      >
        <cue-icon [icon]="ICONS.cueTablet" [theme]="activeBarItem() === 'control' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.control | transloco }}</span>
      </button>
    }

    @if (devices()?.length > 0) {
      <button
        (click)="setActiveBarItem('device')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'device',
        }"
      >
        <cue-icon [icon]="ICONS.cueSpeaker" [theme]="activeBarItem() === 'device' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.device | transloco }}</span>
      </button>
    }

    @if (dialogData.mode === 'anonymous') {
      <button
        (click)="setActiveBarItem('more')"
        [ngClass]="{
          'bottom-bar-item': true,
          active: activeBarItem() === 'more',
        }"
      >
        <cue-icon [icon]="ICONS.cueUser" [theme]="activeBarItem() === 'more' ? 'primary-500' : 'neutral'" />
        <span>{{ translation.multiview.tabs.login | transloco }}</span>
      </button>
    }
  </div>
}

<!--<p>Multiview works!</p>-->

<!--{{ resource() | json }}-->

<!--{{ news.value() | json }}-->
